import type { ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { Edit, IconButton } from 'qampo-ui-library';
import TrashIcon from '../../../assets/icons/trash.svg?react';
import { Tag, Tooltip } from '../../UI';
import type { TableColumn } from '../../UI';
import type { BookingTime } from '../../../api/generated';
import {
  isRecurringTime,
  isSingleOccurrenceTime,
  splitSingleOccurrence,
  timeWindowParts
} from './bookingTime';
import { formatRecurrencePattern, formatRecurrencePeriod } from './bookingFormat';
import styles from './bookings.module.css';

// Placeholder shown wherever a cell has no value to render.
export const EMPTY = '—';

// Resolves a booking's entity ids to display names, dropping any that no longer resolve (e.g. the
// entity was removed after the booking). Accepts the generated `Set<string>` or a plain array.
export const resolveEntityNames = (
  ids: Iterable<string>,
  nameById: Map<string, string>
): string[] =>
  Array.from(ids)
    .map((id) => nameById.get(id))
    .filter((name): name is string => Boolean(name));
// Names beyond this collapse into a "+N" badge with the full list on hover, keeping rows scannable.
const MAX_VISIBLE_NAMES = 2;
const ICON_SIZE = 14;
const ACTIONS_COL_WIDTH = 100;
const DATE_COL_WIDTH = 200;
const REPEATS_COL_WIDTH = 180;
const TIME_COL_WIDTH = 140;

interface EntityNameCellProps {
  /** Resolved entity names (already looked up + filtered by the caller). */
  names: string[];
  /**
   * When set, the booking targets a whole group (e.g. "all staff") rather than
   * named entities — rendered as a single tag instead of a name list.
   */
  allLabel?: string | null;
}

// Shared "who/what the booking is for" cell: a group tag, a name, or a truncated
// name list with a "+N" hover tooltip. Used by every module's first column.
export const entityNameCell = ({ names, allLabel }: EntityNameCellProps): ReactNode => {
  if (allLabel) return <Tag label={allLabel} />;
  if (names.length === 0) return <span className={styles.muted}>{EMPTY}</span>;
  if (names.length <= MAX_VISIBLE_NAMES) {
    return <span className={styles.nameCell}>{names.join(', ')}</span>;
  }
  const visible = names.slice(0, MAX_VISIBLE_NAMES).join(', ');
  const overflow = names.length - MAX_VISIBLE_NAMES;
  return (
    <Tooltip
      trigger={
        <span className={styles.nameCell}>
          {visible} <span className={styles.muted}>+{overflow}</span>
        </span>
      }
      content={<span className={styles.tooltipContent}>{names.join(', ')}</span>}
    />
  );
};

// Start–end window for a booking's time, or the all-day label / placeholder.
export const renderTimeWindow = (t: TFunction, time: BookingTime): string => {
  if (isRecurringTime(time)) {
    const window = timeWindowParts(time.timeWindow);
    return window ? `${window.startTime} – ${window.endTime}` : t('booking-all-day');
  }
  if (isSingleOccurrenceTime(time)) {
    const parts = splitSingleOccurrence(time);
    return `${parts.startTime} – ${parts.endTime}`;
  }
  return EMPTY;
};

// The date (single) or recurrence period (recurring) a booking spans.
export const renderDatePeriod = (t: TFunction, time: BookingTime): string => {
  if (isRecurringTime(time)) return formatRecurrencePeriod(t, time);
  if (isSingleOccurrenceTime(time)) return splitSingleOccurrence(time).date;
  return EMPTY;
};

type HasBookingTime = { time: BookingTime };

// Standard date / repeats / time columns shared by the occurrence-based booking
// types (patient, staff, room). Closed-day and reserved-time model time
// differently and build their own columns.
export const standardDateColumn = <T extends HasBookingTime>(
  t: TFunction,
  headerKey: string
): TableColumn<T> => ({
  key: 'date',
  header: t(headerKey),
  width: DATE_COL_WIDTH,
  render: (booking) => <span>{renderDatePeriod(t, booking.time)}</span>
});

export const standardRepeatsColumn = <T extends HasBookingTime>(
  t: TFunction,
  headerKey: string
): TableColumn<T> => ({
  key: 'repeats',
  header: t(headerKey),
  width: REPEATS_COL_WIDTH,
  render: (booking) =>
    isRecurringTime(booking.time) ? (
      <span>{formatRecurrencePattern(t, booking.time)}</span>
    ) : (
      <span className={styles.muted}>{EMPTY}</span>
    )
});

export const standardTimeColumn = <T extends HasBookingTime>(
  t: TFunction,
  headerKey: string
): TableColumn<T> => ({
  key: 'time',
  header: t(headerKey),
  width: TIME_COL_WIDTH,
  render: (booking) => <span>{renderTimeWindow(t, booking.time)}</span>
});

// Free-text reason column shared by every booking type.
export const reasonColumn = <T extends { reason?: string }>(
  t: TFunction,
  headerKey: string
): TableColumn<T> => ({
  key: 'reason',
  header: t(headerKey),
  render: (booking) =>
    booking.reason ? <span>{booking.reason}</span> : <span className={styles.muted}>{EMPTY}</span>
});

interface ActionsColumnHandlers<T> {
  editKey: string;
  deleteKey: string;
  onEdit: (booking: T) => void;
  onDelete: (booking: T) => void;
}

// Trailing edit/delete action column, identical across every booking type.
export const actionsColumn = <T,>(
  t: TFunction,
  { editKey, deleteKey, onEdit, onDelete }: ActionsColumnHandlers<T>
): TableColumn<T> => ({
  key: 'actions',
  header: '',
  width: ACTIONS_COL_WIDTH,
  headerAlign: 'center',
  render: (booking) => (
    <div className={styles.rowActions}>
      <span title={t(editKey)} aria-label={t(editKey)}>
        <IconButton
          size="small"
          icon={<Edit width={ICON_SIZE} height={ICON_SIZE} />}
          onClick={() => onEdit(booking)}
          classes={styles.actionBtn}
        />
      </span>
      <span title={t(deleteKey)} aria-label={t(deleteKey)}>
        <IconButton
          size="small"
          icon={<TrashIcon width={ICON_SIZE} height={ICON_SIZE} />}
          onClick={() => onDelete(booking)}
          classes={`${styles.actionBtn} ${styles.actionBtnDelete}`}
        />
      </span>
    </div>
  )
});
